// Template configuration system for storefront layouts

export type TemplateId = 'modern' | 'classic' | 'bold' | 'minimal'

export interface TemplateConfig {
  id: TemplateId
  name: string
  description: string
  icon: string // Lucide icon name
  bestFor: string
  features: string[]

  homepage: {
    defaultView: 'cards' | 'list' | 'locations' | 'map'
    availableViews: ('cards' | 'list' | 'locations' | 'map')[]
    showHeroBanner: boolean
    heroHeight: 'compact' | 'normal' | 'tall'
    gridColumns: {
      sm: number
      md: number
      lg: number
      xl: number
    }
    cardSpacing: 'tight' | 'normal' | 'relaxed'
  }

  eventCard: {
    layout: 'vertical' | 'horizontal'
    imageRatio: 'square' | '16:9' | '4:3' | '3:2'
    showDescription: boolean
    hoverEffect: 'lift' | 'scale' | 'none'
  }

  eventDetails: {
    layout: 'sidebar' | 'stacked' | 'floating'
    bookingPanelPosition: 'right' | 'bottom' | 'overlay'
  }

  checkout: {
    layout: 'split' | 'stacked'
    progressStyle: 'steps' | 'bar' | 'minimal'
    formWidth: 'narrow' | 'normal' | 'wide'
  }
}

/** Individual layout overrides stored in branding.layout */
export interface LayoutOverrides {
  template?: string | null
  defaultView?: string | null
  availableViews?: string[]
  showHeroBanner?: boolean
  heroHeight?: string
  gridLayout?: string       // '4-column' | '2-column' | 'list'
  cardSpacing?: string
  cardLayout?: string
  imageRatio?: string
  showCardDescription?: boolean
  hoverEffect?: string
  detailsLayout?: string
  bookingPanelPosition?: string
  checkoutLayout?: string
  progressStyle?: string
  formWidth?: string
  seatedSeriesCalendar?: boolean
  // When 'page', clicking a location card on the homepage routes to
  // /locations/[city] (a dedicated city-specific landing). When 'expand'
  // (default) the card toggles inline. Marketing teams use the page
  // mode for shareable city-specific CTAs.
  locationsClickBehavior?: 'expand' | 'page'
  // Ordered list of city slugs (kebab-case) pinned to the top of the
  // Locations view. Unknown slugs are ignored. Everything not in this
  // list renders alphabetically beneath the featured set.
  featuredLocations?: string[]
  // When true, show a "Locations" dropdown in the storefront navbar
  // (desktop only) listing pinned cities + a Browse all link.
  showLocationsNav?: boolean
  // Existing layout properties (passed through, not used by template)
  [key: string]: unknown
}

const GRID_COLUMNS_MAP: Record<string, TemplateConfig['homepage']['gridColumns']> = {
  '4-column': { sm: 1, md: 2, lg: 3, xl: 4 },
  '2-column': { sm: 1, md: 1, lg: 2, xl: 2 },
  'list':     { sm: 1, md: 1, lg: 1, xl: 1 },
}

const TEMPLATES: Record<TemplateId, TemplateConfig> = {
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Clean, contemporary grid layout with subtle animations',
    icon: 'Zap',
    bestFor: 'Tech conferences, contemporary events, modern venues',
    features: ['Grid layout', 'Card-based design', 'Subtle animations', 'Full-width hero'],

    homepage: {
      defaultView: 'cards',
      availableViews: ['cards', 'list', 'locations', 'map'],
      showHeroBanner: true,
      heroHeight: 'normal',
      gridColumns: {
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4
      },
      cardSpacing: 'normal'
    },

    eventCard: {
      layout: 'vertical',
      imageRatio: '16:9',
      showDescription: false,
      hoverEffect: 'lift'
    },

    eventDetails: {
      layout: 'sidebar',
      bookingPanelPosition: 'right'
    },

    checkout: {
      layout: 'split',
      progressStyle: 'steps',
      formWidth: 'normal'
    }
  },

  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional, information-dense list layout',
    icon: 'Star',
    bestFor: 'Theater, performing arts, classical music venues',
    features: ['List layout', 'Detailed info', 'Traditional design', 'Readable typography'],

    homepage: {
      defaultView: 'list',
      availableViews: ['cards', 'list', 'locations', 'map'],
      showHeroBanner: false,
      heroHeight: 'compact',
      gridColumns: {
        sm: 1,
        md: 1,
        lg: 1,
        xl: 1
      },
      cardSpacing: 'tight'
    },

    eventCard: {
      layout: 'horizontal',
      imageRatio: '4:3',
      showDescription: true,
      hoverEffect: 'none'
    },

    eventDetails: {
      layout: 'stacked',
      bookingPanelPosition: 'bottom'
    },

    checkout: {
      layout: 'stacked',
      progressStyle: 'bar',
      formWidth: 'normal'
    }
  },

  bold: {
    id: 'bold',
    name: 'Bold',
    description: 'Large imagery with dramatic, high-impact styling',
    icon: 'Sparkles',
    bestFor: 'Music festivals, sports events, entertainment venues',
    features: ['Large images', 'Dramatic styling', 'High contrast', 'Visual impact'],

    homepage: {
      defaultView: 'cards',
      availableViews: ['cards', 'list', 'locations', 'map'],
      showHeroBanner: true,
      heroHeight: 'tall',
      gridColumns: {
        sm: 1,
        md: 1,
        lg: 2,
        xl: 2
      },
      cardSpacing: 'relaxed'
    },

    eventCard: {
      layout: 'vertical',
      imageRatio: '16:9',
      showDescription: false,
      hoverEffect: 'scale'
    },

    eventDetails: {
      layout: 'floating',
      bookingPanelPosition: 'overlay'
    },

    checkout: {
      layout: 'split',
      progressStyle: 'bar',
      formWidth: 'wide'
    }
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Maximum whitespace with elegant, refined simplicity',
    icon: 'Layout',
    bestFor: 'Art galleries, design events, premium experiences',
    features: ['Generous whitespace', 'Photography-focused', 'Elegant design', 'Refined aesthetics'],

    homepage: {
      defaultView: 'cards',
      availableViews: ['cards', 'list', 'locations', 'map'],
      showHeroBanner: false,
      heroHeight: 'compact',
      gridColumns: {
        sm: 1,
        md: 1,
        lg: 2,
        xl: 2
      },
      cardSpacing: 'relaxed'
    },

    eventCard: {
      layout: 'vertical',
      imageRatio: 'square',
      showDescription: false,
      hoverEffect: 'none'
    },

    eventDetails: {
      layout: 'stacked',
      bookingPanelPosition: 'bottom'
    },

    checkout: {
      layout: 'stacked',
      progressStyle: 'minimal',
      formWidth: 'narrow'
    }
  }
}

/**
 * Get template configuration — accepts either a template ID string (backward compat)
 * or a layout overrides object with individual settings.
 */
export function getTemplateConfig(layoutOrId?: string | LayoutOverrides | null): TemplateConfig {
  // String or null — old behavior, return preset directly
  if (typeof layoutOrId === 'string' || layoutOrId == null) {
    const id = (layoutOrId as TemplateId) || 'modern'
    return TEMPLATES[id] || TEMPLATES.modern
  }

  // Layout object — build config from individual overrides
  const layout = layoutOrId
  const base = TEMPLATES[(layout.template as TemplateId)] || TEMPLATES.modern

  return {
    ...base,
    homepage: {
      ...base.homepage,
      ...(layout.defaultView ? { defaultView: layout.defaultView as TemplateConfig['homepage']['defaultView'] } : {}),
      ...(layout.availableViews?.length ? { availableViews: layout.availableViews as TemplateConfig['homepage']['availableViews'] } : {}),
      ...(layout.showHeroBanner !== undefined ? { showHeroBanner: layout.showHeroBanner } : {}),
      ...(layout.heroHeight ? { heroHeight: layout.heroHeight as TemplateConfig['homepage']['heroHeight'] } : {}),
      ...(layout.gridLayout && GRID_COLUMNS_MAP[layout.gridLayout] ? { gridColumns: GRID_COLUMNS_MAP[layout.gridLayout] } : {}),
      ...(layout.cardSpacing ? { cardSpacing: layout.cardSpacing as TemplateConfig['homepage']['cardSpacing'] } : {}),
    },
    eventCard: {
      ...base.eventCard,
      ...(layout.cardLayout ? { layout: layout.cardLayout as TemplateConfig['eventCard']['layout'] } : {}),
      ...(layout.imageRatio ? { imageRatio: layout.imageRatio as TemplateConfig['eventCard']['imageRatio'] } : {}),
      ...(layout.showCardDescription !== undefined ? { showDescription: layout.showCardDescription } : {}),
      ...(layout.hoverEffect ? { hoverEffect: layout.hoverEffect as TemplateConfig['eventCard']['hoverEffect'] } : {}),
    },
    eventDetails: {
      ...base.eventDetails,
      ...(layout.detailsLayout ? { layout: layout.detailsLayout as TemplateConfig['eventDetails']['layout'] } : {}),
      ...(layout.bookingPanelPosition ? { bookingPanelPosition: layout.bookingPanelPosition as TemplateConfig['eventDetails']['bookingPanelPosition'] } : {}),
    },
    checkout: {
      ...base.checkout,
      ...(layout.checkoutLayout ? { layout: layout.checkoutLayout as TemplateConfig['checkout']['layout'] } : {}),
      ...(layout.progressStyle ? { progressStyle: layout.progressStyle as TemplateConfig['checkout']['progressStyle'] } : {}),
      ...(layout.formWidth ? { formWidth: layout.formWidth as TemplateConfig['checkout']['formWidth'] } : {}),
    },
  }
}

/**
 * Get all available templates
 */
export function getAllTemplates(): TemplateConfig[] {
  return Object.values(TEMPLATES)
}

/**
 * Check if a template ID is valid
 */
export function isValidTemplateId(templateId: string): templateId is TemplateId {
  return templateId in TEMPLATES
}

/**
 * Get flat layout overrides from a template preset (for populating admin UI)
 */
export function getLayoutFromPreset(templateId: TemplateId): LayoutOverrides {
  const t = TEMPLATES[templateId] || TEMPLATES.modern
  return {
    template: templateId,
    defaultView: t.homepage.defaultView,
    availableViews: [...t.homepage.availableViews],
    showHeroBanner: t.homepage.showHeroBanner,
    heroHeight: t.homepage.heroHeight,
    gridLayout: t.homepage.gridColumns.xl === 4 ? '4-column' :
                t.homepage.gridColumns.xl === 2 ? '2-column' : 'list',
    cardSpacing: t.homepage.cardSpacing,
    cardLayout: t.eventCard.layout,
    imageRatio: t.eventCard.imageRatio,
    showCardDescription: t.eventCard.showDescription,
    hoverEffect: t.eventCard.hoverEffect,
    detailsLayout: t.eventDetails.layout,
    bookingPanelPosition: t.eventDetails.bookingPanelPosition,
    checkoutLayout: t.checkout.layout,
    progressStyle: t.checkout.progressStyle,
    formWidth: t.checkout.formWidth,
  }
}
